import React, { useEffect, useRef, useState } from 'react';
import { loadStripe, Stripe, StripeCardElement, StripeElements } from '@stripe/stripe-js';
import { paymentApi } from '../services/api';

/**
 * Stripe Elements card form. Captures the card, tokenizes it (so we get the
 * funding type before charging — required to legally skip surcharge on debit),
 * and confirms a backend-issued PaymentIntent. Handles 3DS via Stripe.js.
 *
 * Used by multi-invoice pay; will also back the single-invoice / deposit /
 * parts / payment-link flows in Phase 3.
 */

type CardFunding = 'credit' | 'debit' | 'prepaid' | 'unknown';

export interface StripeCardFormProps {
  /** Base amount the dealer is paying in dollars (before surcharge). */
  baseAmount: number;
  /**
   * Async function the component calls AFTER tokenizing the card. Receives
   * `{paymentMethodId, cardFunding}` and must return a clientSecret + the
   * decided amounts so the UI can render the breakdown + confirm the intent.
   */
  createIntent: (args: {
    paymentMethodId: string;
    cardFunding: CardFunding;
  }) => Promise<{
    success: boolean;
    clientSecret?: string;
    paymentIntentId?: string;
    baseAmount?: number;
    surchargeAmount?: number;
    surchargePercent?: number;
    totalAmount?: number;
    error?: string;
  }>;
  /** Called after the PaymentIntent successfully confirms. */
  onSuccess: (result: { paymentIntentId: string; surchargeAmount: number; totalAmount: number }) => void;
  /** Disclosure copy shown above the card field. */
  disclosure?: string;
  submitLabel?: string;
}

let _stripePromise: Promise<Stripe | null> | null = null;
function getStripePromise(): Promise<Stripe | null> {
  if (!_stripePromise) {
    _stripePromise = paymentApi
      .getStripePublicKey()
      .then((k) => (k?.publishableKey ? loadStripe(k.publishableKey) : null));
  }
  return _stripePromise;
}

export const StripeCardForm: React.FC<StripeCardFormProps> = ({
  baseAmount,
  createIntent,
  onSuccess,
  disclosure,
  submitLabel,
}) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [breakdown, setBreakdown] = useState<{
    baseAmount: number;
    surchargeAmount: number;
    surchargePercent: number;
    totalAmount: number;
    cardFunding: CardFunding;
  } | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);
  // Set once the card is verified + the surcharge is computed. Its presence
  // flips the form from "enter card" to "review total & confirm" — the charge
  // only fires on the second (Confirm) click, so the dealer sees the exact
  // total (incl. any card fee) BEFORE any money moves.
  const [pendingIntent, setPendingIntent] = useState<{
    clientSecret: string;
    paymentIntentId: string;
    paymentMethodId: string;
  } | null>(null);
  const cardMountRef = useRef<HTMLDivElement | null>(null);

  // Load Stripe + create elements + mount card.
  useEffect(() => {
    let mounted = true;
    getStripePromise().then((s) => {
      if (!mounted || !s) return;
      setStripe(s);
      const els = s.elements();
      setElements(els);
      const card = els.create('card', {
        style: {
          base: {
            fontSize: '15px',
            color: '#ffffff',
            '::placeholder': { color: '#9ca3af' },
            iconColor: '#9ca3af',
          },
          invalid: { color: '#ef4444', iconColor: '#ef4444' },
        },
      });
      setCardElement(card);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!cardElement || !cardMountRef.current) return;
    cardElement.mount(cardMountRef.current);
    cardElement.on('ready', () => setCardReady(true));
    cardElement.on('change', (e) => {
      setCardComplete(e.complete);
      setCardError(e.error?.message || null);
    });
    return () => { cardElement.unmount(); };
  }, [cardElement]);

  // Step 1 (first click): tokenize the card, get the funding type, and have the
  // backend compute the surcharge + create the PaymentIntent. NO charge yet —
  // we only show the breakdown so the dealer can review the exact total.
  const handleVerify = async () => {
    if (!stripe || !elements || !cardElement) return;
    setGenericError(null);
    setSubmitting(true);
    try {
      const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      if (pmErr || !paymentMethod) {
        setGenericError(pmErr?.message || 'Card could not be processed');
        return;
      }
      const funding = (paymentMethod.card?.funding as CardFunding) || 'unknown';

      const intent = await createIntent({ paymentMethodId: paymentMethod.id, cardFunding: funding });
      if (!intent.success || !intent.clientSecret || !intent.paymentIntentId) {
        setGenericError(intent.error || 'Failed to set up payment');
        return;
      }
      setBreakdown({
        baseAmount: intent.baseAmount ?? baseAmount,
        surchargeAmount: intent.surchargeAmount ?? 0,
        surchargePercent: intent.surchargePercent ?? 0,
        totalAmount: intent.totalAmount ?? baseAmount,
        cardFunding: funding,
      });
      setPendingIntent({
        clientSecret: intent.clientSecret,
        paymentIntentId: intent.paymentIntentId,
        paymentMethodId: paymentMethod.id,
      });
      // Lock the card field so the method can't drift from what we quoted.
      cardElement.update({ disabled: true });
    } catch (err: any) {
      setGenericError(err?.message || 'Payment failed unexpectedly');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2 (second click): now that the dealer has seen the exact total incl.
  // any card fee, confirm the PaymentIntent — this is the actual charge.
  const handleConfirmPay = async () => {
    if (!stripe || !pendingIntent) return;
    setGenericError(null);
    setSubmitting(true);
    try {
      const { paymentIntent, error: confirmErr } = await stripe.confirmCardPayment(
        pendingIntent.clientSecret,
        { payment_method: pendingIntent.paymentMethodId },
      );
      if (confirmErr || !paymentIntent) {
        setGenericError(confirmErr?.message || 'Payment was declined');
        return;
      }
      if (paymentIntent.status !== 'succeeded') {
        setGenericError(`Payment ${paymentIntent.status} — please try again or use a different card`);
        return;
      }
      onSuccess({
        paymentIntentId: paymentIntent.id,
        surchargeAmount: breakdown?.surchargeAmount ?? 0,
        totalAmount: breakdown?.totalAmount ?? baseAmount,
      });
    } catch (err: any) {
      setGenericError(err?.message || 'Payment failed unexpectedly');
    } finally {
      setSubmitting(false);
    }
  };

  // Back out of the review step to change cards (abandons the pending intent).
  const handleBack = () => {
    if (submitting) return;
    setBreakdown(null);
    setPendingIntent(null);
    setGenericError(null);
    cardElement?.update({ disabled: false });
  };

  const fmt = (n: number) =>
    `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

  return (
    <div className="space-y-3">
      {disclosure && (
        <div className="text-xs p-3 rounded border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
          {disclosure}
        </div>
      )}

      <div>
        <label className="text-xs text-gray-400 block mb-1">Card details</label>
        <div
          ref={cardMountRef}
          className="px-3 py-3 rounded border"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-hover)', minHeight: 46 }}
        />
        {!cardReady && <p className="text-xs text-gray-500 mt-1">Loading secure card form…</p>}
        {cardError && <p className="text-xs text-red-500 mt-1">{cardError}</p>}
      </div>

      {breakdown && (
        <div className="rounded border p-3 text-sm" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex justify-between">
            <span className="text-gray-400">Subtotal</span>
            <span className="font-mono text-white">{fmt(breakdown.baseAmount)}</span>
          </div>
          {breakdown.surchargeAmount > 0 ? (
            <div className="flex justify-between mt-1">
              <span className="text-gray-400">
                Card processing fee ({breakdown.surchargePercent.toFixed(1)}%)
              </span>
              <span className="font-mono text-white">{fmt(breakdown.surchargeAmount)}</span>
            </div>
          ) : breakdown.cardFunding === 'debit' || breakdown.cardFunding === 'prepaid' ? (
            <div className="flex justify-between mt-1">
              <span className="text-gray-400">Card processing fee</span>
              <span className="text-green-500 text-xs">Waived (debit card)</span>
            </div>
          ) : null}
          <div className="flex justify-between mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <span className="font-semibold text-white">Total to be charged</span>
            <span className="font-mono font-bold text-white">{fmt(breakdown.totalAmount)}</span>
          </div>
        </div>
      )}

      {genericError && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-sm">{genericError}</div>
      )}

      {!pendingIntent ? (
        <button
          onClick={handleVerify}
          disabled={submitting || !cardReady || !cardComplete}
          className="w-full px-5 py-2.5 text-sm rounded font-semibold bg-keder-yellow text-black hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Verifying card…' : 'Continue'}
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-gray-400 text-center">
            Review the total above — you won't be charged until you confirm.
          </p>
          <button
            onClick={handleConfirmPay}
            disabled={submitting}
            className="w-full px-5 py-2.5 text-sm rounded font-semibold bg-keder-yellow text-black hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? 'Processing…'
              : `${submitLabel ? submitLabel + ' — ' : 'Confirm & Pay '}${fmt(breakdown?.totalAmount ?? baseAmount)}`}
          </button>
          <button
            onClick={handleBack}
            disabled={submitting}
            className="w-full px-5 py-2 text-xs rounded border text-gray-300 hover:bg-white/5 disabled:opacity-50"
            style={{ borderColor: 'var(--border-color)' }}
          >
            Use a different card
          </button>
        </div>
      )}
      <p className="text-[11px] text-gray-500 text-center">
        Card secured by Stripe. We never see or store your card number.
      </p>
    </div>
  );
};

export default StripeCardForm;
